/**
 * Presentational component for the Optimist connection section on the Profile page.
 */

import { OtpInput, Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { useRef, useState } from "react";
import { Button, SpinningLoader } from "risa-oasis-ui_v2";

const CODE_PATTERN = /^\d{6}$/;

/** Props for {@link OptimistConnectionSection}. */
interface OptimistConnectionSectionProps {
  /** Whether the facility has the feature on; when off only disabling is offered */
  facilityEnabled: boolean;
  /** Whether the user's TOTP seed is currently shared with Optimist */
  connected: boolean;
  /** Whether a request is in flight */
  loading: boolean;
  /** Whether the last enable attempt failed because MFA is not enrolled */
  mfaNotEnrolled: boolean;
  /** Message for a wrong, reused, or locked-out authenticator code */
  codeError: string;
  /** Called with the authenticator code; resolves true on success */
  onEnable: (code: string) => Promise<boolean>;
  /** Called when the user clicks Disable */
  onDisable: () => void;
}

/**
 * Renders the Optimist connection status and the enable (with code) / disable actions.
 */
export function OptimistConnectionSection({
  facilityEnabled,
  connected,
  loading,
  mfaNotEnrolled,
  codeError,
  onEnable,
  onDisable,
}: OptimistConnectionSectionProps) {
  const [prompting, setPrompting] = useState(false);
  const [code, setCode] = useState("");
  const submitting = useRef(false);

  // OtpInput fires onChange and onComplete in one tick, so the code arrives as an argument.
  const confirmEnable = async (value: string = code) => {
    if (submitting.current || !CODE_PATTERN.test(value)) return;
    submitting.current = true;
    try {
      if (await onEnable(value)) setPrompting(false);
      setCode("");
    } finally {
      submitting.current = false;
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row items-center gap-2">
        <div className="text-base font-semibold">Optimist connection</div>
        <Tags
          label={connected ? "Enabled" : "Disabled"}
          backgroundColor={connected ? "#E6F3F0" : "#F3F4F6"}
          textColor={connected ? "#166534" : "#6B7280"}
          showBorder={true}
        />
      </div>
      <div className="text-sm text-gray-600">
        {facilityEnabled
          ? "Let Optimist sign in to One RISA on your behalf without asking for your authenticator code."
          : "Your facility has turned this off. You can still disable your existing connection."}
      </div>
      {mfaNotEnrolled && !connected && (
        <div className="text-sm text-gray-600">
          Set up two-factor authentication first. Sign out and sign in again to
          enroll, then return here to enable this.
        </div>
      )}
      {prompting && !connected ? (
        <div className="flex flex-col gap-2">
          <OtpInput
            value={code}
            onChange={setCode}
            length={6}
            label="Current authenticator code"
            required
            error={codeError}
            onComplete={confirmEnable}
            disabled={loading}
            autoFocus
          />
          <div className="flex flex-row gap-2">
            <Button
              disabled={loading || !CODE_PATTERN.test(code)}
              buttonType="primary"
              size="medium"
              onClick={() => confirmEnable()}
            >
              Confirm
              {loading && <SpinningLoader />}
            </Button>
            <Button
              disabled={loading}
              buttonType="secondary"
              size="medium"
              onClick={() => {
                setPrompting(false);
                setCode("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Button
            disabled={loading || (mfaNotEnrolled && !connected)}
            buttonType={connected ? "secondary" : "primary"}
            size="medium"
            onClick={connected ? onDisable : () => setPrompting(true)}
          >
            {connected ? "Disable" : "Enable"}
            {loading && <SpinningLoader />}
          </Button>
        </div>
      )}
    </div>
  );
}
